import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { AdminBriefs } from "@/components/AdminBriefs";
import { Star, Search, Plus, X, ArrowUp, ArrowDown, Check, Send, KeyRound, UserCheck, UserX, Trash2 } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("");
  const [selected, setSelected] = useState([]);
  const [savingF, setSavingF] = useState(false);
  const [kajabi, setKajabi] = useState(null);
  const [accessEmail, setAccessEmail] = useState("");
  const [accessBusy, setAccessBusy] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [resyncing, setResyncing] = useState(false);
  const [hookUrl, setHookUrl] = useState("");
  const [connecting, setConnecting] = useState(false);

  const connectKajabi = async () => {
    setConnecting(true);
    try {
      await api.post("/admin/kajabi/config", { hook_url: hookUrl });
      toast.success("Kajabi sync connected. New signups will flow across automatically.");
      setHookUrl("");
      const kj = await api.get("/admin/kajabi-status").catch(() => ({ data: null }));
      setKajabi(kj.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the hook URL"); }
    finally { setConnecting(false); }
  };

  const deleteMember = async (prof) => {
    if (!window.confirm(`Permanently delete ${prof.name}'s account, card and files? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/members/${prof.user_id}`);
      toast.success(`${prof.name} deleted.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const resyncKajabi = async () => {
    setResyncing(true);
    try {
      const res = await api.post("/admin/kajabi/resync", {});
      toast.success(`Pushed ${res.data.attempted} signup${res.data.attempted === 1 ? "" : "s"} to Kajabi (${res.data.synced_total} synced total).`);
      const kj = await api.get("/admin/kajabi-status").catch(() => ({ data: null }));
      setKajabi(kj.data);
    } catch { toast.error("Backlog sync failed"); }
    finally { setResyncing(false); }
  };

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/");
  }, [user, loading, navigate]);

  const load = async () => {
    const [pf, ft, kj, mb] = await Promise.all([
      api.get("/admin/profiles"),
      api.get("/admin/featured"),
      api.get("/admin/kajabi-status").catch(() => ({ data: null })),
      api.get("/admin/members").catch(() => ({ data: { members: [] } })),
    ]);
    setProfiles(pf.data.profiles || []);
    setMonth(ft.data.month || "");
    setSelected(ft.data.user_ids || []);
    setKajabi(kj.data);
    setMembers(mb.data.members || []);
  };
  useEffect(() => { if (user?.is_admin) load(); /* eslint-disable-next-line */ }, [user]);

  const byId = Object.fromEntries(profiles.map((p) => [p.user_id, p]));
  const filtered = profiles.filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()));

  const addWriter = (id) => {
    if (selected.includes(id)) return;
    if (selected.length >= 5) { toast.error("You can feature up to 5 writers"); return; }
    setSelected([...selected, id]);
  };
  const removeWriter = (id) => setSelected(selected.filter((s) => s !== id));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= selected.length) return;
    const next = [...selected];
    [next[i], next[j]] = [next[j], next[i]];
    setSelected(next);
  };

  const saveFeatured = async () => {
    setSavingF(true);
    try {
      await api.put("/admin/featured", { month, user_ids: selected });
      toast.success("Featured Writers updated & live.");
    } catch { toast.error("Save failed"); }
    finally { setSavingF(false); }
  };

  const setAccess = async (email, action) => {
    const e = (email || "").trim().toLowerCase();
    if (!e) { toast.error("Enter a member's email"); return; }
    setAccessBusy(true);
    try {
      const res = await api.post("/admin/access", { email: e, action });
      if (action === "grant") {
        toast.success(res.data.has_account
          ? `Access granted to ${e}.`
          : `Access reserved for ${e}. It unlocks the moment they sign up with this email.`);
      } else {
        toast(`Access revoked for ${e}.`);
      }
      setAccessEmail("");
      const mb = await api.get("/admin/members").catch(() => ({ data: { members: [] } }));
      setMembers(mb.data.members || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "That didn't work — check the email");
    } finally { setAccessBusy(false); }
  };

  const bulkGrant = async () => {
    if (!bulkText.trim()) { toast.error("Paste some emails first"); return; }
    setAccessBusy(true);
    try {
      const res = await api.post("/admin/access/bulk", { text: bulkText });
      const d = res.data;
      toast.success(`Granted access to ${d.granted} member${d.granted === 1 ? "" : "s"}${d.pending_signup ? ` (${d.pending_signup} pending signup)` : ""}.`);
      setBulkText("");
      setBulkOpen(false);
      const mb = await api.get("/admin/members").catch(() => ({ data: { members: [] } }));
      setMembers(mb.data.members || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No valid emails found");
    } finally { setAccessBusy(false); }
  };

  if (loading || !user?.is_admin) {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><span className="font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Loading…</span></div>;
  }

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-24 pb-20">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Curation</span>
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase text-cream mt-2 mb-2">Editor's desk</h1>
        <p className="text-ash mb-10 text-sm">Set this month's Featured Writers and manage member access. Changes go live on the main page instantly.</p>

        {/* Kajabi sync status */}
        {kajabi && (
          <div data-testid="kajabi-status-panel" className="border border-line bg-surface p-5 mb-8 flex flex-wrap items-center gap-x-8 gap-y-3">
            <div className="flex items-center gap-2 font-heading text-lg font-bold uppercase text-cream tracking-tight"><Send size={16} className="text-gold" /> Kajabi Sync</div>
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide">
              <span className="text-ash">Status:</span>
              <span className={kajabi.mode === "off" ? "text-ash" : "text-gold"}>{kajabi.mode === "off" ? "Not connected" : `Active (${kajabi.mode})`}</span>
            </div>
            <div className="font-mono text-xs uppercase tracking-wide text-ash">Signups captured: <span className="text-cream">{kajabi.total}</span></div>
            <div className="font-mono text-xs uppercase tracking-wide text-ash">Synced: <span className="text-cream">{kajabi.synced}</span></div>
            {kajabi.failed > 0 && <div className="font-mono text-xs uppercase tracking-wide text-destructive">Failed: {kajabi.failed}</div>}
            {kajabi.mode !== "off" && kajabi.total - kajabi.synced > 0 && (
              <button
                onClick={resyncKajabi}
                disabled={resyncing}
                data-testid="kajabi-resync-btn"
                className="inline-flex items-center gap-2 border border-gold text-gold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
              >
                <Send size={13} /> {resyncing ? "Syncing…" : `Sync backlog (${kajabi.total - kajabi.synced})`}
              </button>
            )}
            {kajabi.mode === "off" && (
              <div className="w-full flex flex-col sm:flex-row gap-2 mt-1">
                <input
                  data-testid="kajabi-hook-input"
                  value={hookUrl}
                  onChange={(e) => setHookUrl(e.target.value)}
                  placeholder="Paste your Zapier catch-hook URL (https://hooks.zapier.com/hooks/catch/…)"
                  className="flex-1 bg-ink border border-line text-cream text-sm px-4 py-2.5 focus:outline-none focus:border-gold placeholder:text-ash/50"
                />
                <button
                  onClick={connectKajabi}
                  disabled={connecting || !hookUrl.trim()}
                  data-testid="kajabi-connect-btn"
                  className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-gold-hi transition-colors disabled:opacity-50"
                >
                  <Send size={13} /> {connecting ? "Connecting…" : "Connect"}
                </button>
              </div>
            )}
            {kajabi.mode === "off" && <span className="font-mono text-[11px] text-ash/70 normal-case tracking-normal">Every signup is being stored — paste your Zapier catch-hook above and new signups push to your Kajabi form automatically.</span>}
          </div>
        )}

        {/* Member access — manual grant/revoke (no Zapier needed) */}
        <div data-testid="member-access-panel" className="border border-line bg-surface p-6 md:p-8 mb-8">
          <div className="flex items-center gap-2 font-heading text-2xl font-bold uppercase text-cream tracking-tight mb-2"><KeyRound size={20} className="text-gold" /> Member Access</div>
          <p className="text-ash text-sm mb-5">Grant or revoke full browsing access by email — instant, no Zapier required. Use this for ACCESS Premium / Mastermind members, or anyone you comp. Granting works even before they sign up (it unlocks automatically when they register with that email).</p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <input
              data-testid="access-email-input"
              type="email"
              value={accessEmail}
              onChange={(e) => setAccessEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setAccess(accessEmail, "grant"); }}
              placeholder="member@email.com"
              className="flex-1 bg-ink border border-line text-cream px-4 py-3 focus:outline-none focus:border-gold placeholder:text-ash/50"
            />
            <button onClick={() => setAccess(accessEmail, "grant")} disabled={accessBusy} data-testid="grant-access-btn" className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors disabled:opacity-60"><UserCheck size={15} /> Grant</button>
            <button onClick={() => setAccess(accessEmail, "revoke")} disabled={accessBusy} data-testid="revoke-access-btn" className="inline-flex items-center justify-center gap-2 border border-line text-cream font-bold uppercase tracking-widest text-xs px-6 py-3 hover:border-destructive hover:text-destructive transition-colors disabled:opacity-60"><UserX size={15} /> Revoke</button>
          </div>

          {/* Bulk import */}
          <div className="mb-6">
            <button onClick={() => setBulkOpen(!bulkOpen)} data-testid="bulk-import-toggle" className="font-mono text-[11px] uppercase tracking-wide text-ash hover:text-gold transition-colors inline-flex items-center gap-1.5">
              <Plus size={13} className={`transition-transform ${bulkOpen ? "rotate-45" : ""}`} /> Import a list — paste multiple emails at once
            </button>
            {bulkOpen && (
              <div className="mt-3">
                <textarea
                  data-testid="bulk-import-textarea"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={5}
                  placeholder={"Paste emails any way you like — one per line, comma-separated, even copied straight from a spreadsheet or email thread. We'll pick out the addresses.\n\njane@example.com, mike@example.com\nSarah Lee <sarah@example.com>"}
                  className="w-full bg-ink border border-line text-cream text-sm px-4 py-3 focus:outline-none focus:border-gold placeholder:text-ash/50 resize-y"
                />
                <button onClick={bulkGrant} disabled={accessBusy} data-testid="bulk-grant-btn" className="mt-2 inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors disabled:opacity-60">
                  <UserCheck size={15} /> Grant access to all
                </button>
              </div>
            )}
          </div>

          <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-2 block">Members with access ({members.length})</label>
          {members.length === 0 ? (
            <p className="text-ash text-sm border border-dashed border-line p-4">No members with access yet. Grant one above.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-line divide-y divide-line">
              {members.map((m) => (
                <div key={m.email} data-testid={`member-row-${m.email}`} className="flex items-center gap-3 p-3 bg-ink">
                  <span className="flex-1 text-sm text-cream truncate">{m.email}</span>
                  {!m.has_account && <span className="font-mono text-[9px] uppercase tracking-wider text-ash border border-line px-1.5 py-0.5">Pending signup</span>}
                  <span className="font-mono text-[10px] uppercase tracking-wider text-gold">{m.tier}</span>
                  <button onClick={() => setAccess(m.email, "revoke")} disabled={accessBusy} data-testid={`revoke-member-${m.email}`} className="text-ash hover:text-destructive p-1" aria-label="Revoke access"><UserX size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <AdminBriefs />

        <div className="grid lg:grid-cols-2 gap-px bg-line border border-line">
          {/* Featured Writers */}
          <div className="bg-surface p-6 md:p-8 flex flex-col gap-6">
            <div className="flex items-center gap-2 font-heading text-2xl font-bold uppercase text-cream tracking-tight"><Star size={20} className="text-gold" /> Featured Writers</div>

            <div>
              <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-2 block">Month label</label>
              <input data-testid="featured-month-input" value={month} onChange={(e) => setMonth(e.target.value)} placeholder="e.g. July 2026" className="w-full bg-ink border border-line text-cream px-4 py-3 focus:outline-none focus:border-gold placeholder:text-ash/50" />
            </div>

            {/* Selected */}
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-2 block">Selected ({selected.length}/5) — order shown on site</label>
              {selected.length === 0 ? (
                <p className="text-ash text-sm border border-dashed border-line p-4">No writers selected yet. Add up to 5 below.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {selected.map((id, i) => (
                    <div key={id} data-testid={`selected-writer-${i}`} className="flex items-center gap-3 border border-line bg-ink p-2">
                      <span className="font-mono text-[11px] text-gold w-6 text-center">0{i + 1}</span>
                      <div className="w-9 h-9 bg-surface overflow-hidden shrink-0">{mediaUrl(byId[id]?.avatar) && <img src={mediaUrl(byId[id]?.avatar)} alt="" className="w-full h-full object-cover" />}</div>
                      <span className="flex-1 text-sm text-cream truncate">{byId[id]?.name || id}</span>
                      <button onClick={() => move(i, -1)} className="text-ash hover:text-gold p-1"><ArrowUp size={14} /></button>
                      <button onClick={() => move(i, 1)} className="text-ash hover:text-gold p-1"><ArrowDown size={14} /></button>
                      <button onClick={() => removeWriter(id)} data-testid={`remove-writer-${i}`} className="text-ash hover:text-destructive p-1"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Picker */}
            <div>
              <div className="flex items-center gap-2 border border-line bg-ink px-3 mb-2">
                <Search size={15} className="text-ash" />
                <input data-testid="writer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search writers…" className="flex-1 bg-transparent py-2.5 text-cream text-sm focus:outline-none placeholder:text-ash/50" />
              </div>
              <div className="max-h-60 overflow-y-auto border border-line divide-y divide-line">
                {filtered.map((p) => (
                  <div key={p.user_id} className="flex items-center gap-3 p-2 bg-ink">
                    <div className="w-8 h-8 bg-surface overflow-hidden shrink-0">{mediaUrl(p.avatar) && <img src={mediaUrl(p.avatar)} alt="" className="w-full h-full object-cover" />}</div>
                    <span className="flex-1 text-sm text-cream truncate">{p.name} <span className="text-ash/60 text-xs">· {(p.roles || [])[0]}</span></span>
                    <button onClick={() => addWriter(p.user_id)} disabled={selected.includes(p.user_id)} data-testid={`add-writer-${p.user_id}`} className="inline-flex items-center gap-1 border border-line text-ash font-mono text-[10px] uppercase px-2 py-1 hover:border-gold hover:text-gold transition-colors disabled:opacity-40"><Plus size={12} /> Add</button>
                    <button onClick={() => deleteMember(p)} data-testid={`delete-member-${p.user_id}`} title="Delete this member permanently" className="text-ash hover:text-destructive p-1 transition-colors"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={saveFeatured} disabled={savingF} data-testid="save-featured-btn" className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs py-3.5 hover:bg-gold-hi transition-colors disabled:opacity-60"><Check size={15} /> {savingF ? "Saving…" : "Save Featured Writers"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
